import { cartMutation, runCartMutation, subscribeCartMutation, notifyCartChanged } from './wt-cart-actions.js';

const HANDLE = /^[a-z0-9][a-z0-9-]*$/;
const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
const cents = value => Number.isSafeInteger(value) && value >= 0;
const normalized = value => String(value).trim().replace(/\s+/g, ' ').toLowerCase();
const boundedCount = (value, fallback, maximum) => positiveInteger(value) ? Math.min(value, maximum) : fallback;

export function bundleSettings(config = {}) {
  return {
    relatedEnabled: config.relatedEnabled !== false,
    readyBundlesEnabled: config.readyBundlesEnabled !== false,
    customBundleEnabled: config.customBundleEnabled !== false,
    bundlePosition: ['gallery', 'after_product', 'explore'].includes(config.bundlePosition) ? config.bundlePosition : 'explore',
    descriptionPosition: ['after_product', 'purchase_column'].includes(config.descriptionPosition) ? config.descriptionPosition : 'gallery',
    relatedCount: boundedCount(config.relatedCount, 4, 4),
    readyBundleCount: boundedCount(config.readyBundleCount, 3, 3),
    customBundleOpen: config.customBundleOpen === true
  };
}

export function quantityRule(variant) {
  const rule = variant.quantity_rule;
  if (rule === undefined || rule === null) return { min: 1, max: null, increment: 1 };
  if (!positiveInteger(rule.min) || !positiveInteger(rule.increment)
    || (rule.max !== null && rule.max !== undefined && (!positiveInteger(rule.max) || rule.max < rule.min))) {
    throw new Error('Invalid product quantity rules.');
  }
  return { min: rule.min, increment: rule.increment, max: rule.max ?? null };
}

export function validQuantity(variant, value) {
  const rule = quantityRule(variant);
  return positiveInteger(value) && value >= rule.min && (rule.max === null || value <= rule.max)
    && (value - rule.min) % rule.increment === 0;
}

export function normalizeProduct(raw, expectedHandle) {
  if (!raw || !positiveInteger(raw.id) || typeof raw.title !== 'string' || !HANDLE.test(raw.handle)
    || (expectedHandle && raw.handle !== expectedHandle) || !Array.isArray(raw.variants) || !raw.variants.length) {
    throw new Error('Incomplete product response.');
  }
  const plans = new Map();
  for (const group of raw.selling_plan_groups || []) {
    for (const plan of group.selling_plans || []) {
      if (!positiveInteger(plan.id) || typeof plan.name !== 'string') throw new Error('Incomplete selling plan response.');
      plans.set(plan.id, plan);
    }
  }
  const variants = raw.variants.map(variant => {
    if (!positiveInteger(variant.id) || typeof variant.title !== 'string' || typeof variant.available !== 'boolean' || !cents(variant.price)) {
      throw new Error('Incomplete variant response.');
    }
    quantityRule(variant);
    const allocations = (variant.selling_plan_allocations || []).map(allocation => {
      if (!positiveInteger(allocation.selling_plan_id) || !cents(allocation.price)) throw new Error('Incomplete selling plan price.');
      return { ...allocation, plan: plans.get(allocation.selling_plan_id) };
    });
    const breaks = (variant.quantity_price_breaks || []).map(priceBreak => {
      if (!positiveInteger(priceBreak.minimum_quantity) || !cents(priceBreak.price)) throw new Error('Incomplete quantity price.');
      return priceBreak;
    });
    return { ...variant, quantity_price_breaks: breaks, allocations,
      requiresPlan: raw.requires_selling_plan === true || variant.requires_selling_plan === true };
  });
  return { ...raw, variants };
}

export function planSignature(plan) {
  // Match actual option semantics, not a guessed frequency parsed from marketing text.
  if (plan?.recurring_deliveries !== true || !Array.isArray(plan.options) || !plan.options.length) return null;
  if (plan.options.some(option => typeof option.name !== 'string' || !option.name.trim()
    || typeof option.value !== 'string' || !option.value.trim())) return null;
  // App quantity-bound plans need separate app-level compatibility verification, including value "0".
  if (plan.options.some(option => /\bquantity\b/i.test(option.name))) return null;
  return JSON.stringify(plan.options.map(option => [normalized(option.name), normalized(option.value)])
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))));
}

export function commonPlans(selections) {
  if (selections.length < 2 || selections.length > 3) return [];
  const bySelection = selections.map(selection => {
    const options = new Map();
    for (const allocation of selection.variant.allocations) {
      const signature = planSignature(allocation.plan);
      // Duplicate semantic plans within one SKU are ambiguous; never choose one invisibly.
      if (signature && !options.has(signature)) options.set(signature, allocation);
      else if (signature) options.set(signature, null);
    }
    return options;
  });
  return [...bySelection[0]].filter(([signature, allocation]) => allocation
    && bySelection.every(options => options.get(signature))).map(([signature]) => ({
      signature,
      allocations: bySelection.map(options => options.get(signature)),
      label: [...new Set(bySelection.map(options => options.get(signature).plan.name))].join(' / ')
    }));
}

export function unitPrice(variant, quantity, allocation) {
  if (allocation) return allocation.price;
  return variant.quantity_price_breaks.filter(priceBreak => quantity >= priceBreak.minimum_quantity)
    .sort((left, right) => right.minimum_quantity - left.minimum_quantity)[0]?.price ?? variant.price;
}

export function bundleItems(selections, plan, metadata) {
  if (selections.length < 2 || selections.length > 3 || new Set(selections.map(selection => selection.product.id)).size !== selections.length) {
    throw new Error('Select two or three different products.');
  }
  if (plan && (plan.allocations.length !== selections.length
    || !commonPlans(selections).some(candidate => candidate.signature === plan.signature
      && candidate.allocations.every((allocation, index) => allocation.selling_plan_id === plan.allocations[index].selling_plan_id)))) {
    throw new Error('The selected plan is not available for all products.');
  }
  if (metadata && (typeof metadata.label !== 'string' || !metadata.label.trim() || metadata.label.length > 160
    || typeof metadata.group !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(metadata.group))) throw new Error('Invalid chew-box grouping.');
  return selections.map((selection, index) => {
    if (!selection.variant.available || !validQuantity(selection.variant, selection.quantity)) throw new Error('Invalid selected quantity or unavailable variant.');
    if (selection.variant.requiresPlan && !plan) throw new Error('A selling plan is required.');
    const item = { id: selection.variant.id, quantity: selection.quantity };
    if (plan) item.selling_plan = plan.allocations[index].selling_plan_id;
    if (metadata) item.properties = { 'Chew box': metadata.label, _wt_bundle_group: metadata.group };
    return item;
  });
}

export function bundleSubtotal(selections, plan) {
  return selections.reduce((total, selection, index) => total
    + unitPrice(selection.variant, selection.quantity, plan?.allocations[index]) * selection.quantity, 0);
}

export function readyBundleSelections(products, limit = 3) {
  const count = boundedCount(limit, 3, 3);
  const eligible = product => {
    if (!product || !positiveInteger(product.id) || product.requires_selling_plan === true) return null;
    const variant = product.variants.find(candidate => candidate.available
      && !candidate.requiresPlan && candidate.requires_selling_plan !== true);
    if (!variant) return null;
    const quantity = quantityRule(variant).min;
    return validQuantity(variant, quantity) ? { product, variant, quantity } : null;
  };
  const current = eligible(products[0]);
  if (!current) return [];
  const seen = new Set([current.product.id]);
  const offers = [];
  for (const product of products.slice(1)) {
    if (seen.has(product.id)) continue;
    seen.add(product.id);
    const addition = eligible(product);
    if (addition) offers.push([current, addition]);
    if (offers.length === count) break;
  }
  return offers;
}

export function routeRoot(root) {
  if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//') || /[?#\\]/.test(root)) {
    throw new Error('Invalid storefront locale route.');
  }
  return root.endsWith('/') ? root : `${root}/`;
}

export class CartRequestError extends Error {
  constructor(message, uncertain) { super(message); this.uncertain = uncertain; }
}

export async function addItems(root, items, fetcher = fetch) {
  let response;
  try {
    response = await fetcher(`${routeRoot(root)}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
  } catch (error) {
    throw new CartRequestError(error.message, true);
  }
  let result;
  try { result = await response.json(); }
  catch (error) { throw new CartRequestError(error.message, true); }
  if (!response.ok) throw new CartRequestError(typeof result.description === 'string' ? result.description : '', false);
  const confirmed = Array.isArray(result?.items) && result.items.length > 0 && items.length > 0 && items.every(requested =>
    result.items.some(returned => {
      const variantId = returned?.variant_id ?? returned?.id;
      const planId = returned?.selling_plan_allocation?.selling_plan?.id ?? returned?.selling_plan;
      const group = requested.properties?._wt_bundle_group;
      return variantId === requested.id && positiveInteger(returned.quantity) && returned.quantity >= requested.quantity
        // Shopify may return a merged line quantity, but its purchase mode and bundle group must match.
        && (requested.selling_plan === undefined ? planId === undefined || planId === null : planId === requested.selling_plan)
        && (group === undefined || returned.properties?._wt_bundle_group === group);
    }));
  if (!confirmed) throw new CartRequestError('Unexpected cart response.', true);
  return result;
}

export function placeRelatedSection(section, productId, page = document) {
  const originalParent = section.parentNode;
  const originalNext = section.nextSibling;
  let anchor = null;
  const placement = {
    get anchor() { return anchor; },
    refresh() {
      const main = page.getElementById('MainContent');
      const box = main ? page.querySelector(`[data-pf-type="ProductBox"][data-product-id="${productId}"]`) : null;
      const candidate = box?.closest('[data-pf-type="Section"]');
      anchor = candidate && main?.contains(candidate) && !candidate.closest('form') ? candidate : null;
      if (main && !main.closest('form')) {
        if (anchor && anchor.nextElementSibling !== section) anchor.after(section);
        else if (!anchor && main.lastElementChild !== section) main.append(section);
      }
    },
    restore() {
      if (originalParent?.isConnected) originalParent.insertBefore(section, originalNext?.parentNode === originalParent ? originalNext : null);
    }
  };
  placement.refresh();
  return placement;
}

export function placeBundleByGallery(bundle, feedback, productId, page = document) {
  const original = [bundle, feedback].map(node => ({ node, parent: node.parentNode, next: node.nextSibling }));
  let mounted;
  function structure() {
    const main = page.getElementById('MainContent');
    const box = main ? page.querySelector(`[data-pf-type="ProductBox"][data-product-id="${productId}"]`) : null;
    const parent = box?.parentElement;
    if (!box || !main.contains(box) || !parent || box.closest('form') || parent.closest('form') || box.closest('.wt-description-stage')) return null;
    const form = box.querySelector('form');
    const row = form?.querySelector(':scope > [data-pf-type="Row"]');
    const gallery = box.querySelector('[data-pf-type="ProductImage"]');
    const title = box.querySelector('[data-pf-type="ProductTitle"]');
    const galleryColumn = gallery?.closest('[data-pf-type="Column"]');
    const purchaseColumn = title?.closest('[data-pf-type="Column"]');
    const galleryCell = galleryColumn?.parentElement;
    const purchaseCell = purchaseColumn?.parentElement;
    if (!form || !row || !gallery || !title || gallery.closest('form') !== form || title.closest('form') !== form
      || galleryColumn === purchaseColumn || galleryCell === purchaseCell || row.children.length !== 2
      || galleryCell?.parentElement !== row || purchaseCell?.parentElement !== row
      || !galleryCell.classList.contains('pf-c') || !purchaseCell.classList.contains('pf-c')) return null;
    return { box, form, row, gallery, title, galleryCell, purchaseCell, parent };
  }
  function restore() {
    if (!mounted) return;
    const { stage, slot, box, parent, next } = mounted;
    for (const position of [...original].reverse()) {
      if (position.parent && slot.contains(position.node)) {
        position.parent.insertBefore(position.node, position.next?.parentNode === position.parent ? position.next : null);
      }
    }
    if (box.parentNode === stage && parent.isConnected && stage.isConnected) {
      parent.insertBefore(box, next?.parentNode === parent ? next : stage.parentNode === parent ? stage : null);
    }
    // Preserve any PageFly replacement nodes that appeared inside our neutral wrapper.
    while (stage.firstChild) {
      const child = stage.firstChild;
      if (!stage.parentNode || (child === slot && !slot.children.length)) child.remove();
      else stage.before(child);
    }
    stage.remove();
    mounted = undefined;
  }
  return {
    get stage() { return mounted?.stage; },
    get slot() { return mounted?.slot; },
    get box() { return mounted?.box; },
    refresh() {
      const candidate = bundle.hidden ? null : structure();
      if (mounted && candidate && mounted.box === candidate.box && mounted.form === candidate.form
        && mounted.row === candidate.row && mounted.gallery === candidate.gallery && mounted.title === candidate.title
        && mounted.galleryCell === candidate.galleryCell && mounted.purchaseCell === candidate.purchaseCell
        && candidate.box.parentElement === mounted.stage && mounted.stage.isConnected
        && mounted.slot.parentElement === mounted.stage && !mounted.slot.closest('form')) {
        if (!mounted.stage.classList.contains('wt-product-stage')) mounted.stage.classList.add('wt-product-stage');
        for (const name of ['wt-bundle-slot', 'wt-related-bundles']) {
          if (!mounted.slot.classList.contains(name)) mounted.slot.classList.add(name);
        }
        if (bundle.parentNode !== mounted.slot) mounted.slot.append(bundle);
        if (feedback.parentNode !== mounted.slot) mounted.slot.append(feedback);
        return;
      }
      restore();
      const target = bundle.hidden ? null : structure();
      if (!target || bundle.closest('form') || feedback.closest('form')) return;
      const stage = page.createElement('div');
      stage.className = 'wt-product-stage';
      stage.setAttribute('data-wt-product-id', String(productId));
      const slot = page.createElement('div');
      slot.id = 'WtBundleSlot';
      slot.className = 'wt-bundle-slot wt-related-bundles';
      const next = target.box.nextSibling;
      target.parent.insertBefore(stage, target.box);
      stage.append(target.box, slot);
      slot.append(bundle, feedback);
      mounted = { ...target, stage, slot, next };
    },
    restore
  };
}

export function placeBundleExplorer(bundle, feedback, productId, strings, page = document) {
  const original = [bundle, feedback].map(node => ({ node, parent: node.parentNode, next: node.nextSibling }));
  const dialog = page.createElement('dialog'); dialog.id = 'WtBundleExplorer';
  dialog.className = 'wt-bundle-explorer wt-related-bundles'; dialog.setAttribute('aria-labelledby', 'WtBundleExplorerTitle');
  const header = page.createElement('div'); header.className = 'wt-bundle-explorer__header';
  const heading = page.createElement('h2'); heading.id = 'WtBundleExplorerTitle'; heading.textContent = strings.explorerTitle;
  const closeButton = page.createElement('button'); closeButton.type = 'button';
  closeButton.className = 'wt-bundle-explorer__close wt-related-bundles__secondary'; closeButton.textContent = strings.close; closeButton.autofocus = true;
  header.append(heading, closeButton); dialog.append(header);
  const trigger = page.createElement('button'); trigger.type = 'button'; trigger.className = 'wt-bundle-explore';
  trigger.textContent = strings.explore; trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-controls', dialog.id); trigger.setAttribute('aria-expanded', 'false');
  const fallback = page.createElement('a'); fallback.className = 'wt-bundle-explore'; fallback.textContent = strings.explore;
  if (!bundle.id) bundle.id = 'WtBundleOptions';
  fallback.href = `#${bundle.id}`;
  const events = new AbortController();
  let destroyed = false;
  let active = false;
  function restoreNode(position) {
    if (!position.parent) return;
    const next = position.next?.parentNode === position.parent ? position.next : null;
    if (position.node.parentNode !== position.parent || position.node.nextSibling !== next) position.parent.insertBefore(position.node, next);
  }
  function restoreFeedback() { if (dialog.contains(feedback)) restoreNode(original[1]); }
  function close(returnFocus = true) {
    if (dialog.open) dialog.close();
    restoreFeedback();
    if (trigger.getAttribute('aria-expanded') !== 'false') trigger.setAttribute('aria-expanded', 'false');
    if (returnFocus && trigger.isConnected) trigger.focus();
  }
  function restore() {
    close(false);
    for (const position of [...original].reverse()) if (dialog.contains(position.node)) restoreNode(position);
    trigger.remove(); fallback.remove(); dialog.remove(); active = false;
  }
  function open() {
    if (destroyed || !active || bundle.hidden || dialog.open) return;
    dialog.append(feedback); dialog.showModal(); trigger.setAttribute('aria-expanded', 'true'); closeButton.focus();
  }
  trigger.addEventListener('click', open, { signal: events.signal });
  closeButton.addEventListener('click', () => close(), { signal: events.signal });
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, { signal: events.signal });
  dialog.addEventListener('close', () => {
    restoreFeedback();
    if (trigger.getAttribute('aria-expanded') !== 'false') trigger.setAttribute('aria-expanded', 'false');
  }, { signal: events.signal });
  return {
    get dialog() { return active ? dialog : null; },
    get trigger() { return active ? trigger : null; },
    close,
    refresh() {
      if (destroyed) return;
      if (bundle.hidden) { restore(); return; }
      const main = page.getElementById('MainContent');
      const box = main ? page.querySelector(`[data-pf-type="ProductBox"][data-product-id="${productId}"]`) : null;
      const form = box?.querySelector('form');
      const title = box?.querySelector('[data-pf-type="ProductTitle"]');
      const purchase = title?.closest('[data-pf-type="Column"]');
      const add = box?.querySelector('[data-pf-type="ProductATC"]');
      const host = page.body || main;
      const supported = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
      const safe = supported && host?.isConnected && !host.closest('form') && purchase && add
        && add.closest('form') === form && purchase.closest('form') === form && purchase.contains(add);
      if (!safe) {
        restore();
        if (bundle.parentNode && !bundle.closest('form') && bundle.previousElementSibling !== fallback) bundle.before(fallback);
        return;
      }
      fallback.remove();
      if (dialog.parentNode !== host) host.append(dialog);
      if (bundle.parentNode !== dialog) dialog.append(bundle);
      const anchor = purchase.querySelector('.wt-product-purchase-info') || add;
      if (anchor.nextElementSibling !== trigger) anchor.after(trigger);
      active = true;
      if (dialog.open) { if (feedback.parentNode !== dialog) dialog.append(feedback); }
      else restoreFeedback();
    },
    restore,
    destroy() { restore(); destroyed = true; events.abort(); }
  };
}

let bundleSequence = 0;
function bundleMetadata(label) {
  const group = typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID()
    : `wt-${Date.now().toString(36)}-${(++bundleSequence).toString(36)}-${Math.random().toString(36).slice(2)}`;
  return { label, group };
}

let currentController;
let contentObserver;
let observedContent;
let lifecycleSuspended = false;
// A replaced view must not reset the gate for an in-flight or unconfirmed cart write.

function mountRelatedBundles() {
  const section = document.getElementById('WtRelatedBundles');
  const dataElement = document.getElementById('WtRelatedBundleData');
  if (lifecycleSuspended) return;
  if (currentController?.section === section && currentController.dataElement === dataElement
    && currentController.dataText === dataElement?.textContent
    && section?.isConnected && currentController.bundle.contains(currentController.form)) {
    currentController.refreshPlacement();
    currentController.resumeInitialLoad();
    return;
  }
  currentController?.destroy();
  currentController = undefined;
  if (!section || !dataElement) return;
  let config;
  try {
    config = JSON.parse(dataElement.textContent);
    if (!positiveInteger(config.currentProductId) || !HANDLE.test(config.productHandle) || !config.strings
      || Object.values(config.strings).some(value => typeof value !== 'string')) throw new Error('Incomplete related-product settings.');
    config.root = routeRoot(config.root);
    Object.assign(config, bundleSettings(config));
  } catch (error) {
    console.error('WhiteTail related products could not start.', error);
    return;
  }
  section.hidden = !config.relatedEnabled && !config.readyBundlesEnabled && !config.customBundleEnabled;
  if (section.hidden) return;
  const strings = config.strings;
  const formatter = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });
  const money = value => formatter.format(value / 100);
  const translated = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) =>
    text.replaceAll(`__WT_${name.toUpperCase()}__`, String(value))
      .replaceAll(`{{${name}}}`, String(value)).replaceAll(`{{ ${name} }}`, String(value)), strings[key]);
  const status = section.querySelector('[data-wt-related-status]');
  const retry = section.querySelector('[data-wt-related-retry]');
  const cards = section.querySelector('[data-wt-related-cards]');
  const bundle = section.querySelector('[data-wt-bundle]');
  const form = section.querySelector('#WtBundleForm');
  const rows = section.querySelector('[data-wt-bundle-products]');
  const count = section.querySelector('[data-wt-bundle-count]');
  const oneTime = section.querySelector('#WtBundleOneTime');
  const subscription = section.querySelector('#WtBundleSubscription');
  const planControls = section.querySelector('[data-wt-bundle-plan-controls]');
  const planSelect = section.querySelector('[data-wt-bundle-plan]');
  const planNote = section.querySelector('[data-wt-bundle-plan-note]');
  const recurring = section.querySelector('[data-wt-bundle-recurring]');
  const total = section.querySelector('[data-wt-bundle-subtotal]');
  const savings = section.querySelector('[data-wt-bundle-savings]');
  const addBundle = section.querySelector('[data-wt-bundle-add]');
  const cartStatus = section.querySelector('[data-wt-cart-status]');
  const cartLink = section.querySelector('[data-wt-bundle-cart]');
  const feedback = section.querySelector('.wt-related-bundles__feedback');
  const readyCards = bundle?.querySelector('[data-wt-ready-bundles]');
  const relatedHeader = section.querySelector('[data-wt-related-header]');
  const readyHeader = bundle?.querySelector('[data-wt-ready-header]');
  const customBundle = bundle?.querySelector('[data-wt-custom-bundle]');
  if ([status, retry, cards, bundle, form, rows, count, oneTime, subscription, planControls, planSelect,
    planNote, recurring, total, savings, addBundle, cartStatus, cartLink, feedback].some(element => !element)) {
    console.error('WhiteTail related-product markup is incomplete.');
    return;
  }
  const placement = placeRelatedSection(section, config.currentProductId);
  const galleryPlacement = config.bundlePosition === 'gallery' && config.descriptionPosition !== 'gallery' && (config.readyBundlesEnabled || config.customBundleEnabled)
    ? placeBundleByGallery(bundle, feedback, config.currentProductId) : { refresh() {}, restore() {} };
  const explorer = config.bundlePosition === 'explore' && (config.readyBundlesEnabled || config.customBundleEnabled)
    ? placeBundleExplorer(bundle, feedback, config.currentProductId, strings) : { refresh() {}, restore() {}, destroy() {}, close() {} };
  cards.hidden = !config.relatedEnabled;
  if (relatedHeader) relatedHeader.hidden = !config.relatedEnabled;
  if (readyHeader) readyHeader.hidden = !config.readyBundlesEnabled;
  if (customBundle) { customBundle.hidden = !config.customBundleEnabled; customBundle.open = config.customBundleOpen; }

  const events = new AbortController();
  const requests = new AbortController();
  const cardStates = [];
  const bundleStates = [];
  const readyStates = [];
  let selectedPlans = [];
  let destroyed = false;
  let loading = false;
  let loadStarted = false;

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function productLink(product) {
    const anchor = element('a', 'wt-related-card__title', product.title);
    const recommendation = product.recommendationUrl;
    const query = typeof recommendation === 'string' && recommendation.startsWith('/') && !recommendation.startsWith('//')
      ? recommendation.split('?')[1] : undefined;
    anchor.href = `${config.root}products/${encodeURIComponent(product.handle)}${query ? `?${query}` : ''}`;
    return anchor;
  }

  function image(product) {
    const preview = product.media?.find(media => media.preview_image?.src)?.preview_image;
    const source = preview?.src || product.featured_image;
    if (typeof source !== 'string' || !/^(https:\/\/|\/\/|\/[^/])/.test(source)) return null;
    const img = element('img');
    img.src = source;
    img.alt = product.title;
    img.width = positiveInteger(preview?.width) ? preview.width : 512;
    img.height = positiveInteger(preview?.height) ? preview.height : 512;
    img.loading = 'lazy';
    img.decoding = 'async';
    return img;
  }

  function stateFor(product) {
    const variant = product.variants.find(candidate => candidate.available) || product.variants[0];
    return { product, variant, quantity: quantityRule(variant).min, selected: false };
  }

  function quantityMessage(state) {
    const rule = quantityRule(state.variant);
    const parts = [];
    if (rule.min !== 1 || rule.increment !== 1) parts.push(translated('quantityRules', rule));
    if (rule.max !== null) parts.push(translated('quantityMax', rule));
    return parts.join(' ');
  }

  function controls(state, prefix, onChange) {
    const wrapper = element('div', 'wt-related-card__controls');
    const variantLabel = element('label', '', strings.variant);
    const select = element('select');
    select.id = `${prefix}-variant-${state.product.id}`;
    variantLabel.htmlFor = select.id;
    select.setAttribute('aria-label', `${strings.variant}: ${state.product.title}`);
    for (const variant of state.product.variants) {
      const option = element('option', '', `${variant.title}${variant.available ? '' : ` - ${strings.soldOut}`}`);
      option.value = String(variant.id);
      option.disabled = !variant.available;
      select.append(option);
    }
    select.value = String(state.variant.id);
    if (state.product.variants.length > 1) wrapper.append(variantLabel, select);
    const quantityLabel = element('label', '', strings.quantity);
    const input = element('input');
    input.type = 'number';
    input.id = `${prefix}-quantity-${state.product.id}`;
    input.inputMode = 'numeric';
    input.setAttribute('aria-label', `${strings.quantity}: ${state.product.title}`);
    quantityLabel.htmlFor = input.id;
    const ruleNote = element('p', 'wt-bundle__note');
    ruleNote.id = `${prefix}-rule-${state.product.id}`;
    input.setAttribute('aria-describedby', ruleNote.id);
    wrapper.append(quantityLabel, input, ruleNote);

    state.updateControls = () => {
      const rule = quantityRule(state.variant);
      input.min = String(rule.min);
      input.step = String(rule.increment);
      if (rule.max === null) input.removeAttribute('max');
      else input.max = String(rule.max);
      const quantityText = Number.isFinite(state.quantity) ? String(state.quantity) : '';
      if (input.value !== quantityText) input.value = quantityText;
      input.disabled = cartMutation.pending || cartMutation.unconfirmed || !state.variant.available || (prefix === 'bundle' && !state.selected);
      select.disabled = cartMutation.pending || cartMutation.unconfirmed || (prefix === 'bundle' && !state.selected);
      ruleNote.textContent = quantityMessage(state);
      ruleNote.hidden = !ruleNote.textContent;
    };
    state.updateControls();
    select.addEventListener('change', () => {
      state.variant = state.product.variants.find(variant => String(variant.id) === select.value);
      state.quantity = quantityRule(state.variant).min;
      state.updateControls();
      onChange();
    }, { signal: events.signal });
    input.addEventListener('input', () => {
      state.quantity = input.valueAsNumber;
      input.setAttribute('aria-invalid', String(!validQuantity(state.variant, state.quantity)));
      onChange();
    }, { signal: events.signal });
    return wrapper;
  }

  function currentSelections() { return bundleStates.filter(state => state.selected); }
  function currentPlan() { return subscription.checked ? selectedPlans.find(plan => plan.signature === planSelect.value) : undefined; }

  function refreshBundle() {
    const selections = currentSelections();
    const previous = planSelect.value;
    selectedPlans = commonPlans(selections);
    const signatures = selectedPlans.map(plan => plan.signature);
    if (JSON.stringify([...planSelect.options].map(option => option.value)) !== JSON.stringify(['', ...signatures])) {
      planSelect.replaceChildren();
      const placeholder = element('option', '', strings.choosePlan);
      placeholder.value = '';
      planSelect.append(placeholder);
      for (const plan of selectedPlans) {
        const option = element('option', '', plan.label);
        option.value = plan.signature;
        planSelect.append(option);
      }
      planSelect.value = signatures.includes(previous) ? previous : '';
    }
    const required = selections.some(selection => selection.variant.requiresPlan);
    oneTime.disabled = cartMutation.pending || cartMutation.unconfirmed || required;
    subscription.disabled = cartMutation.pending || cartMutation.unconfirmed || !selectedPlans.length;
    if (subscription.checked && !selectedPlans.length) subscription.checked = false;
    if (!subscription.checked && !required) oneTime.checked = true;
    if (required) oneTime.checked = false;
    planControls.hidden = !subscription.checked;
    planSelect.disabled = cartMutation.pending || cartMutation.unconfirmed || !subscription.checked;
    recurring.hidden = !subscription.checked;
    count.textContent = translated('selectedCount', { count: selections.length });
    let message = '';
    if (selections.length < 2) message = strings.twoRequired;
    else if (!selectedPlans.length && required) message = strings.planUnavailable;
    else if (required && !subscription.checked) message = strings.planRequired;
    else if (subscription.checked && !currentPlan()) message = strings.choosePlan;
    planNote.textContent = message;
    const valid = selections.length >= 2 && selections.length <= 3
      && selections.every(selection => selection.variant.available && validQuantity(selection.variant, selection.quantity));
    const plan = currentPlan();
    const modeValid = subscription.checked ? Boolean(plan) : !required;
    total.parentElement.hidden = !config.showPriceEstimate;
    total.textContent = config.showPriceEstimate && valid && modeValid ? money(bundleSubtotal(selections, plan)) : '';
    const saved = valid && plan ? bundleSubtotal(selections) - bundleSubtotal(selections, plan) : 0;
    savings.hidden = !config.showPriceEstimate || saved <= 0;
    savings.textContent = config.showPriceEstimate && saved > 0 ? translated('subscriptionSavings', { amount: money(saved) }) : '';
    addBundle.disabled = cartMutation.pending || cartMutation.unconfirmed || !config.customBundleEnabled || !valid || !modeValid;
    addBundle.textContent = cartMutation.pending ? strings.adding : strings.addBundle;
    for (const state of bundleStates) {
      state.checkbox.disabled = cartMutation.pending || cartMutation.unconfirmed || !state.variant.available || (!state.selected && selections.length >= 3);
      state.updateControls();
      state.row.setAttribute('data-selected', String(state.selected));
    }
  }

  function refreshCards() {
    for (const state of cardStates) {
      state.price.hidden = !config.showPriceEstimate && state.variant.available;
      state.price.textContent = state.variant.available ? config.showPriceEstimate ? money(unitPrice(state.variant, state.quantity)) : '' : strings.soldOut;
      state.button.disabled = cartMutation.pending || cartMutation.unconfirmed || state.variant.requiresPlan || !state.variant.available || !validQuantity(state.variant, state.quantity);
      state.button.textContent = cartMutation.pending ? strings.adding : strings.quickAdd;
      state.updateControls();
    }
    for (const state of readyStates) {
      state.button.disabled = cartMutation.pending || cartMutation.unconfirmed;
      state.button.textContent = cartMutation.pending ? strings.adding : strings.addReady;
    }
  }

  function announce(message, isError = false) {
    cartStatus.textContent = message;
    cartStatus.setAttribute('data-error', String(isError));
    currentController?.refreshPlacement();
    if (isError) cartStatus.focus({ preventScroll: true });
  }

  async function submit(items) {
    if (cartMutation.pending || cartMutation.unconfirmed) return;
    announce(strings.adding);
    refreshCards();
    refreshBundle();
    try {
      const result = await runCartMutation(() => addItems(config.root, items));
      explorer.close(false);
      if (destroyed) currentController?.closeExplorer();
      if (!destroyed) announce(strings.added);
      else currentController?.report(strings.added);
      notifyCartChanged(result);
    } catch (error) {
      // A rejected quantity can still add available stock. Inspect the cart before another write.
      console.error('WhiteTail cart addition needs review.', error);
      const message = `${error instanceof CartRequestError && !error.uncertain && error.message ? `${error.message} ` : ''}${error.uncertain ? strings.requestUncertain : strings.addError}`;
      if (!destroyed) announce(message, true);
      else currentController?.report(message, true);
    } finally {
      if (!destroyed) {
        cartLink.hidden = false;
        refreshCards();
        refreshBundle();
      }
      else currentController?.refreshCommerce();
    }
  }

  function renderCard(product) {
    const state = stateFor(product);
    const card = element('article', 'wt-related-card');
    const thumbnailLink = productLink(product);
    thumbnailLink.className = 'wt-related-card__image';
    thumbnailLink.textContent = '';
    thumbnailLink.setAttribute('aria-label', product.title);
    const thumbnail = image(product);
    if (thumbnail) thumbnailLink.append(thumbnail);
    card.append(thumbnailLink, productLink(product));
    state.price = element('p', 'wt-related-card__price');
    card.append(state.price);
    if (product.requires_selling_plan || product.variants.every(variant => variant.requiresPlan)) {
      const note = element('p', 'wt-bundle__note', strings.planRequired);
      const link = productLink(product);
      link.textContent = strings.reviewProduct;
      link.className = 'wt-related-bundles__secondary';
      card.append(note, link);
      state.price.hidden = !config.showPriceEstimate && state.variant.available;
      state.price.textContent = state.variant.available ? config.showPriceEstimate ? money(state.variant.price) : '' : strings.soldOut;
    } else {
      card.append(controls(state, 'related', refreshCards));
      state.button = element('button', 'wt-related-bundles__primary', strings.quickAdd);
      state.button.type = 'button';
      state.button.setAttribute('aria-label', `${strings.quickAdd}: ${product.title}`);
      state.button.addEventListener('click', () => {
        if (state.variant.requiresPlan || !state.variant.available || !validQuantity(state.variant, state.quantity)) {
          announce(state.variant.requiresPlan ? strings.planRequired : strings.quantityInvalid, true);
          return;
        }
        submit([{ id: state.variant.id, quantity: state.quantity }]);
      }, { signal: events.signal });
      card.append(state.button);
      cardStates.push(state);
    }
    return card;
  }

  function renderBundleRow(product, isCurrent) {
    const state = stateFor(product);
    const row = element('div', 'wt-bundle__product');
    state.row = row;
    const include = element('label', 'wt-bundle__include');
    const checkbox = element('input');
    checkbox.type = 'checkbox';
    checkbox.id = `WtBundleInclude-${product.id}`;
    checkbox.setAttribute('aria-label', `${strings.include}: ${product.title}`);
    state.selected = isCurrent && state.variant.available;
    checkbox.checked = state.selected;
    state.checkbox = checkbox;
    include.append(checkbox, element('span', '', strings.include));
    const summary = element('div', 'wt-bundle__product-summary');
    const thumbnail = image(product);
    if (thumbnail) summary.append(thumbnail);
    const heading = element('div');
    if (isCurrent) heading.append(element('p', 'wt-bundle__current', strings.currentProduct));
    heading.append(productLink(product));
    if (!state.variant.available) heading.append(element('p', 'wt-bundle__note', strings.soldOut));
    summary.append(heading);
    row.append(include, summary, controls(state, 'bundle', refreshBundle));
    checkbox.addEventListener('change', () => {
      if (checkbox.checked && currentSelections().length >= 3) {
        checkbox.checked = false;
        announce(strings.selectionLimit, true);
        return;
      }
      state.selected = checkbox.checked;
      refreshBundle();
    }, { signal: events.signal });
    bundleStates.push(state);
    return row;
  }

  function renderReadyBundle(selections, index) {
    const card = element('article', 'wt-ready-bundle');
    const products = element('div', 'wt-ready-bundle__products');
    for (const selection of selections) {
      const product = element('div', 'wt-ready-bundle__product');
      const thumbnail = image(selection.product);
      if (thumbnail) product.append(thumbnail);
      const variantText = selection.product.variants.length === 1 && selection.variant.title === 'Default Title'
        ? '' : `${strings.variant}: ${selection.variant.title} / `;
      product.append(productLink(selection.product), element('p', 'wt-ready-bundle__option',
        `${variantText}${strings.quantity}: ${selection.quantity}`));
      products.append(product);
    }
    const price = element('p', 'wt-ready-bundle__price', config.showPriceEstimate ? money(bundleSubtotal(selections)) : '');
    price.hidden = !config.showPriceEstimate;
    const button = element('button', 'wt-ready-bundle__add wt-related-bundles__primary', strings.addReady);
    button.type = 'button';
    button.setAttribute('aria-label', `${strings.addReady}: ${selections.map(selection => selection.product.title).join(' + ')}`);
    button.addEventListener('click', () => {
      if (cartMutation.pending || cartMutation.unconfirmed) return;
      submit(bundleItems(selections, undefined, bundleMetadata(translated('recommendedTogether', { number: index + 1 }))));
    }, { signal: events.signal });
    card.append(element('h3', 'wt-ready-bundle__title', translated('recommendedTogether', { number: index + 1 })), products, price, button);
    readyStates.push({ button });
    return card;
  }

  async function readJson(url) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: requests.signal });
    if (!response.ok) throw new Error(`Product request failed (${response.status}).`);
    return response.json();
  }

  async function load() {
    if (loading || destroyed || cartMutation.pending || cartMutation.unconfirmed) return;
    loadStarted = true;
    loading = true;
    status.textContent = strings.loading;
    retry.hidden = true;
    bundle.hidden = true;
    try {
      const recommendationCount = Math.max(config.relatedEnabled ? config.relatedCount : 0,
        config.readyBundlesEnabled ? config.readyBundleCount : 0, config.customBundleEnabled ? 4 : 0);
      const [recommendations, current] = await Promise.all([
        readJson(`${config.root}recommendations/products.json?product_id=${config.currentProductId}&limit=${recommendationCount}&intent=related`),
        readJson(`${config.root}products/${encodeURIComponent(config.productHandle)}.js`)
      ]);
      if (!Array.isArray(recommendations.products)) throw new Error('Incomplete product recommendations.');
      const currentProduct = normalizeProduct(current, config.productHandle);
      if (currentProduct.id !== config.currentProductId) throw new Error('Current product did not match.');
      const seen = new Set([currentProduct.id]);
      const recommended = recommendations.products.filter(product => {
        if (!positiveInteger(product.id) || !HANDLE.test(product.handle)) throw new Error('Incomplete product recommendation.');
        if (seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      }).slice(0, recommendationCount);
      const products = await Promise.all(recommended.map(async product => {
        const detail = normalizeProduct(await readJson(`${config.root}products/${encodeURIComponent(product.handle)}.js`), product.handle);
        if (detail.id !== product.id) throw new Error('Recommended product did not match.');
        return { ...detail, recommendationUrl: product.url };
      }));
      if (destroyed) return;
      cardStates.length = 0;
      bundleStates.length = 0;
      readyStates.length = 0;
      cards.replaceChildren(...(config.relatedEnabled ? products.slice(0, config.relatedCount).map(renderCard) : []));
      rows.replaceChildren(...(config.customBundleEnabled
        ? [renderBundleRow(currentProduct, true), ...products.map(product => renderBundleRow(product, false))] : []));
      let hasReadyOffers = false;
      if (readyCards) {
        const offers = config.readyBundlesEnabled ? readyBundleSelections([currentProduct, ...products], config.readyBundleCount) : [];
        readyCards.replaceChildren(...offers.map(renderReadyBundle));
        readyCards.hidden = offers.length === 0;
        hasReadyOffers = offers.length > 0;
      }
      if (readyHeader) readyHeader.hidden = !hasReadyOffers;
      if (customBundle) customBundle.hidden = !config.customBundleEnabled || products.length === 0;
      const hasVisibleProducts = hasReadyOffers || ((config.relatedEnabled || config.customBundleEnabled) && products.length > 0);
      status.textContent = hasVisibleProducts ? '' : strings.empty;
      status.hidden = hasVisibleProducts;
      bundle.hidden = !hasReadyOffers && !(config.customBundleEnabled && products.length > 0);
      bundle.setAttribute('aria-labelledby', hasReadyOffers ? 'WtReadyHeading' : 'WtBundleHeading');
      section.setAttribute('aria-labelledby', config.relatedEnabled ? 'WtRelatedHeading' : hasReadyOffers ? 'WtReadyHeading' : 'WtBundleHeading');
      refreshCards();
      refreshBundle();
      currentController?.refreshPlacement();
    } catch (error) {
      if (destroyed || error.name === 'AbortError') return;
      console.error('WhiteTail related products could not load.', error);
      status.hidden = false;
      status.textContent = strings.loadError;
      retry.hidden = false;
    } finally { loading = false; }
  }

  function resumeInitialLoad() {
    if (!loadStarted && !loading && !destroyed && !lifecycleSuspended && !cartMutation.pending && !cartMutation.unconfirmed) load();
  }

  retry.addEventListener('click', load, { signal: events.signal });
  oneTime.addEventListener('change', refreshBundle, { signal: events.signal });
  subscription.addEventListener('change', refreshBundle, { signal: events.signal });
  planSelect.addEventListener('change', refreshBundle, { signal: events.signal });
  form.addEventListener('submit', event => {
    event.preventDefault();
    if (!config.customBundleEnabled || cartMutation.pending || cartMutation.unconfirmed) return;
    const selections = currentSelections();
    const plan = currentPlan();
    if (subscription.checked && !plan) { announce(strings.choosePlan, true); return; }
    let items;
    try { items = bundleItems(selections, plan, bundleMetadata(strings.bundleTitle)); }
    catch (error) { announce(strings.quantityInvalid, true); console.error('WhiteTail bundle selection is invalid.', error); return; }
    submit(items);
  }, { signal: events.signal });
  currentController = {
    section,
    dataElement,
    dataText: dataElement.textContent,
    bundle,
    form,
    get anchor() { return placement.anchor; },
    get slot() { return galleryPlacement.slot || explorer.dialog; },
    refreshPlacement() {
      if (!lifecycleSuspended) {
        placement.refresh(); galleryPlacement.refresh(); explorer.refresh();
        // A gallery-only bundle leaves no visible content in its original full-width shell.
        section.hidden = !config.relatedEnabled && Boolean(galleryPlacement.slot || (explorer.dialog && !cartStatus.textContent)) && config.showProposals !== true;
      }
    },
    report: announce,
    resumeInitialLoad,
    closeExplorer() { explorer.close(false); },
    refreshCommerce() {
      cartLink.hidden = false;
      refreshCards();
      refreshBundle();
      resumeInitialLoad();
    },
    suspendPlacement() { explorer.restore(); galleryPlacement.restore(); placement.restore(); section.hidden = false; },
    destroy() { destroyed = true; events.abort(); requests.abort(); unsubscribeCart(); explorer.destroy(); galleryPlacement.restore(); placement.restore(); section.hidden = false; }
  };
  const unsubscribeCart = subscribeCartMutation(() => {
    if (!destroyed) { refreshCards(); refreshBundle(); resumeInitialLoad(); }
  });
  if (cartMutation.pending || cartMutation.unconfirmed) {
    announce(cartMutation.pending ? strings.adding : strings.requestUncertain);
    cartLink.hidden = false;
    refreshCards();
    refreshBundle();
  }
  load();
}

if (typeof document !== 'undefined') {
  const start = () => {
    lifecycleSuspended = false;
    const content = document.getElementById('MainContent');
    if (contentObserver && observedContent !== content) { contentObserver.disconnect(); contentObserver = undefined; }
    if (!contentObserver && content && typeof MutationObserver !== 'undefined') {
      contentObserver = new MutationObserver(mountRelatedBundles);
      contentObserver.observe(content, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      observedContent = content;
    }
    mountRelatedBundles();
  };
  const stopObserver = () => { contentObserver?.disconnect(); contentObserver = undefined; observedContent = undefined; };
  start();
  document.addEventListener('DOMContentLoaded', start, { once: true });
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', event => {
    if (currentController && (event.target.contains(currentController.section)
      || (currentController.anchor && event.target.contains(currentController.anchor))
      || (currentController.slot && event.target.contains(currentController.slot)))) {
      lifecycleSuspended = true;
      stopObserver();
      currentController.destroy();
      currentController = undefined;
    }
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      lifecycleSuspended = true;
      stopObserver();
      // Retain the commerce controller/handlers and in-flight guard across BFCache navigation.
      currentController?.suspendPlacement();
    });
    window.addEventListener('pageshow', start);
  }
}
